import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Tabs,
  Form,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  Select,
  notification,
  Table,
  Modal,
  DatePicker,
} from "antd";
import { PlusOutlined, DeleteOutlined, DownloadOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import { setSideTab } from "../../../toolkit/slices/inventories/inventorySlice";
import { FaDownload } from "react-icons/fa";
import dayjs from "dayjs";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const VALID_PREFIXES = ["YS", "HK", "SK", "KH"];
const PREFIX_TO_OWNER = {
  YS: "Yellow Spot",
  HK: "Het",
  SK: "Sandesh",
  KH: "Khushi",
};

const SUFFIX_OPTIONS = [
  { label: "A", value: "A" },
  { label: "B", value: "B" },
  { label: "C", value: "C" },
  { label: "D", value: "D" },
  { label: "A-B", value: "A-B" },
  { label: "C-D", value: "C-D" },
];

const defaultRow = () => ({
  id: Math.random().toString(36).slice(2),
  location: "",
  mediaType: undefined,
  illumination: undefined,
  width: undefined,
  Height: undefined,
  suffix: undefined,
  codeNo: undefined,
  maintenanceRate: undefined,
  maintenanceRateApplicableRange: null,
});

const InventoryMasterContainer = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([defaultRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [prefix, setPrefix] = useState(undefined);
  const [nextSerialNo, setNextSerialNo] = useState(null);
  const [loadingSerial, setLoadingSerial] = useState(false);
  const [mediaTypesList, setMediaTypesList] = useState([]);
  const [masterData, setMasterData] = useState([]);
  const [masterLoading, setMasterLoading] = useState(false);
  const [masterSearch, setMasterSearch] = useState("");
  const [rateCardData, setRateCardData] = useState([]);
  const [rateCardLoading, setRateCardLoading] = useState(false);
  const [rateCardSearch, setRateCardSearch] = useState("");
  const [activeTab, setActiveTab] = useState("1");
  const [downloading, setDownloading] = useState(false);
  const [rateHistoryModalVisible, setRateHistoryModalVisible] = useState(false);
  const [rateHistoryParentRecord, setRateHistoryParentRecord] = useState(null);
  const [rateHistoryEditItem, setRateHistoryEditItem] = useState(null);
  const [rateCardSaving, setRateCardSaving] = useState(false);
  const [rateHistoryForm] = Form.useForm();
  const onTabChange = (key) => setActiveTab(key);

  useEffect(() => {
    dispatch(setSideTab(window.location.pathname));
  }, [dispatch]);

  const fetchInventoryMaster = async (search = "") => {
    setMasterLoading(true);
    try {
      const token = window.localStorage.getItem("token");
      const url = `${apiBaseUrl}/inventoryManagement/getInventoryMaster${search ? `?search=${encodeURIComponent(search)}` : ""}`;
      const resp = await fetch(
        url,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
        },
      );

      const json = await resp.json();

      if (!json?.success) {
        notification.error({
          message: "Error",
          description: json?.message || "Failed to fetch Inventory Master",
        });
        setMasterData([]);
        return;
      }

      setMasterData(Array.isArray(json.data) ? json.data : []);
    } catch (e) {
      notification.error({
        message: "Error",
        description: e?.message || "Failed to fetch Inventory Master",
      });
      setMasterData([]);
    } finally {
      setMasterLoading(false);
    }
  };

  const fetchRateCardData = async (search = "") => {
    setRateCardLoading(true);
    try {
      const token = window.localStorage.getItem("token");
      const url = `${apiBaseUrl}/inventoryManagement/getRateCardData${search ? `?search=${encodeURIComponent(search)}` : ""}`;
      const resp = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          authorization: `Token ${token}`,
        },
      });
      const json = await resp.json();
      if (!json?.success) {
        notification.error({
          message: "Error",
          description: json?.message || "Failed to fetch Rate Card data",
        });
        setRateCardData([]);
        return;
      }
      setRateCardData(Array.isArray(json.data) ? json.data : []);
    } catch (e) {
      notification.error({
        message: "Error",
        description: e?.message || "Failed to fetch Rate Card data",
      });
      setRateCardData([]);
    } finally {
      setRateCardLoading(false);
    }
  };
  const deleteRateHistory = async (parentRecord, rateHistoryId) => {
  try {
    const parentId = parentRecord?._id; // This is the inventoryPriceHistory document _id
    if (!parentId || !rateHistoryId) {
      notification.error({
        message: "Error",
        description: "Invalid data for deletion",
      });
      return;
    }

    const token = window.localStorage.getItem("token");
    const response = await fetch(
      `${apiBaseUrl}/inventoryManagement/deleteRateHistory`,
      {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          authorization: `Token ${token}`,
        },
        body: JSON.stringify({ 
          parentId: parentId,  // The big document _id
          rateHistoryId: rateHistoryId  // The child rate history _id
        }),
      }
    );

    const result = await response.json();

    if (result.success) {
      notification.success({
        message: "Success",
        description: "Rate history deleted successfully",
      });
      fetchRateCardData(rateCardSearch); // Refresh the data
    } else {
      notification.error({
        message: "Error",
        description: result.message || "Failed to delete rate history",
      });
    }
  } catch (error) {
    notification.error({
      message: "Error",
      description: error?.message || "Failed to delete rate history",
    });
  }
};

  const openRateHistoryEdit = (parentRecord, historyItem) => {
    setRateHistoryParentRecord(parentRecord);
    setRateHistoryEditItem(historyItem);
    rateHistoryForm.setFieldsValue({
      maintenanceRate: historyItem?.maintenanceRate ?? undefined,
      startDate: historyItem?.startDate ? dayjs(historyItem.startDate) : undefined,
      endDate: historyItem?.endDate ? dayjs(historyItem.endDate) : undefined,
    });
    setRateHistoryModalVisible(true);
  };

  const openRateHistoryAdd = (parentRecord) => {
    setRateHistoryParentRecord(parentRecord);
    setRateHistoryEditItem(null);
    rateHistoryForm.resetFields();
    setRateHistoryModalVisible(true);
  };

  const closeRateHistoryModal = () => {
    setRateHistoryModalVisible(false);
    setRateHistoryParentRecord(null);
    setRateHistoryEditItem(null);
    rateHistoryForm.resetFields();
  };

  const saveRateHistory = async () => {
    try {
      const values = await rateHistoryForm.validateFields();
      const invId = rateHistoryParentRecord?.invId?._id;
      if (!invId) {
        notification.error({ message: "Error", description: "Invalid inventory." });
        return;
      }
      const isEdit = !!rateHistoryEditItem?._id;
      const startD = values.startDate ? dayjs(values.startDate) : null;
      const endD = values.endDate ? dayjs(values.endDate) : null;
      const body = {
        invId,
        ...(isEdit && { rateHistoryId: rateHistoryEditItem._id }),
        maintenanceRate: Number(values.maintenanceRate),
        startDate: startD ? startD.toISOString() : undefined,
        endDate: endD ? endD.endOf("day").toISOString() : undefined,
      };
      setRateCardSaving(true);
      const token = window.localStorage.getItem("token");
      const resp = await fetch(`${apiBaseUrl}/inventoryManagement/updateRateCardData`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          authorization: `Token ${token}`,
        },
        body: JSON.stringify(body),
      });
      const json = await resp.json();
      if (!json?.success) {
        notification.error({
          message: "Error",
          description: json?.message || "Failed to update rate history",
        });
        return;
      }
      const codeNo = rateHistoryParentRecord?.invId?.CodeNo ?? "";
      notification.success({ message: `Rate Card updated for ${codeNo}` });
      closeRateHistoryModal();
      fetchRateCardData(rateCardSearch);
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({
        message: "Error",
        description: e?.message || "Failed to save",
      });
    } finally {
      setRateCardSaving(false);
    }
  };

  const prevActiveTabRef = useRef(activeTab);

  useEffect(() => {
    if (activeTab === "2") {
      const justSwitchedToMaster = prevActiveTabRef.current !== "2";
      prevActiveTabRef.current = activeTab;
      if (justSwitchedToMaster) {
        fetchInventoryMaster(masterSearch);
        return;
      }
      const t = setTimeout(() => fetchInventoryMaster(masterSearch), 400);
      return () => clearTimeout(t);
    }
    if (activeTab === "3") {
      const justSwitchedToRateCard = prevActiveTabRef.current !== "3";
      prevActiveTabRef.current = activeTab;
      if (justSwitchedToRateCard) {
        fetchRateCardData(rateCardSearch);
        return;
      }
      const t = setTimeout(() => fetchRateCardData(rateCardSearch), 400);
      return () => clearTimeout(t);
    }
    prevActiveTabRef.current = activeTab;
  }, [activeTab, masterSearch, rateCardSearch]);
  const downloadMasterExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ isExcel: "true" });
      if (masterSearch?.trim()) params.set("search", masterSearch.trim());
      const resp = await fetch(
        `${apiBaseUrl}/inventoryManagement/getInventoryMaster?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${window.localStorage.getItem("token")}`,
          },
        },
      );

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Download failed: ${resp.status}`);
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "InventoryMasterReport.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {
      notification.error({
        message: "Error",
        description: e?.message || "Failed to download Excel",
      });
    }
    finally {
      setDownloading(false);
    }
  };

  const downloadRateCardExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ isExcel: "true" });
      if (rateCardSearch?.trim()) params.set("search", rateCardSearch.trim());
      const url = `${apiBaseUrl}/inventoryManagement/getRateCardData?${params.toString()}`;
      const resp = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          authorization: `Token ${window.localStorage.getItem("token")}`,
        },
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Download failed: ${resp.status}`);
      }
      const blob = await resp.blob();
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = urlObj;
      a.download = "RateCardReport.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(urlObj);
    } catch (e) {
      notification.error({
        message: "Error",
        description: e?.message || "Failed to download Excel",
      });
    } finally {
      setDownloading(false);
    }
  };

  const fetchMediaTypes = async () => {
    try {
      const token = window.localStorage.getItem("token");
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getAllMediaTypes`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
        },
      );
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setMediaTypesList(result.data);
      } else {
        setMediaTypesList([]);
      }
    } catch (err) {
      console.error("Failed to fetch media types:", err);
      setMediaTypesList([]);
    }
  };

  useEffect(() => {
    fetchMediaTypes();
  }, []);

  const fetchNextSerialNo = async (selectedPrefix) => {
    if (!selectedPrefix) {
      setNextSerialNo(null);
      return;
    }
    setLoadingSerial(true);
    try {
      const token = window.localStorage.getItem("token");
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/returnNextSerialNo?prefix=${encodeURIComponent(
          selectedPrefix,
        )}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
        },
      );
      const result = await response.json();
      if (result.success && result.nextSerialNo != null) {
        setNextSerialNo(result.nextSerialNo);
      } else {
        setNextSerialNo(null);
        notification.error({
          message: "Error",
          description: result.message || "Failed to get next serial no.",
        });
      }
    } catch (err) {
      setNextSerialNo(null);
      notification.error({
        message: "Error",
        description: err?.message || "Failed to fetch next serial no.",
      });
    } finally {
      setLoadingSerial(false);
    }
  };

  const handlePrefixChange = (value) => {
    setPrefix(value);
    fetchNextSerialNo(value);
  };

  const getCodeNoForRow = (record) => {
    if (!prefix || nextSerialNo == null) return "";
    return record.suffix
      ? `${prefix}-${nextSerialNo}-${record.suffix}`
      : `${prefix}-${nextSerialNo}`;
  };

  useEffect(() => {
    if (prefix && nextSerialNo != null) {
      setRows((prev) => prev.map((r) => ({ ...r, codeNo: undefined })));
    }
  }, [prefix, nextSerialNo]);

  const addRow = () => setRows((prev) => [...prev, defaultRow()]);

  const removeRow = (id) => {
    setRows((prev) =>
      prev.length > 1 ? prev.filter((r) => r.id !== id) : prev,
    );
  };

  const updateRow = (id, fieldOrUpdates, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? typeof fieldOrUpdates === "object" && fieldOrUpdates !== null
            ? { ...r, ...fieldOrUpdates }
            : { ...r, [fieldOrUpdates]: value }
          : r,
      ),
    );
  };

  const handleSubmit = async () => {
    try {
      if (!prefix) {
        notification.warning({
          message: "Validation",
          description: "Please select Prefix.",
        });
        return;
      }
      if (nextSerialNo == null && !loadingSerial) {
        notification.warning({
          message: "Validation",
          description: "Please wait for serial number or select Prefix again.",
        });
        return;
      }

      const { city, area } = await form.validateFields();

      const effectiveCodeNo = (r) =>
        (r.codeNo !== undefined && r.codeNo !== ""
          ? r.codeNo
          : getCodeNoForRow(r)
        )?.trim() || "";

      if (
        rows.some(
          (r) =>
            !r.location?.trim() ||
            r.mediaType == null ||
            r.mediaType === "" ||
            !r.illumination ||
            r.width == null ||
            r.Height == null ||
            !effectiveCodeNo(r) ||
            r.maintenanceRate == null ||
            !r.maintenanceRateApplicableRange ||
            !Array.isArray(r.maintenanceRateApplicableRange) ||
            r.maintenanceRateApplicableRange.length !== 2 ||
            !r.maintenanceRateApplicableRange[0] ||
            !r.maintenanceRateApplicableRange[1],
        )
      ) {
        notification.warning({
          message: "Validation",
          description:
            "Every field is mandatory. Please fill all row fields (Location, Media Type, Illumination, Width, Height, Code No, Maintenance Rate, Maintenance Rate Applicable Duration). Suffix is optional.",
        });
        return;
      }

      const body = {
        city: city?.trim() || "",
        area: area?.trim() || "",
        ownedBy: PREFIX_TO_OWNER[prefix] || "",
        locations: rows.map((r) => r.location?.trim() || ""),
        mediaTypes: rows.map((r) => r.mediaType),
        illuminations: rows.map((r) => r.illumination),
        widths: rows.map((r) => Number(r.width)),
        Height: rows.map((r) => Number(r.Height)),
        codeNos: rows.map((r) =>
          r.codeNo !== undefined && r.codeNo !== ""
            ? r.codeNo
            : getCodeNoForRow(r),
        ),
        maintenanceRates: rows.map((r) => Number(r.maintenanceRate)),
        maintenanceRateApplicableStartDate: rows.map((r) =>
          dayjs(r.maintenanceRateApplicableRange[0]).startOf("day").toISOString(),
        ),
        maintenanceRateApplicableEndDate: rows.map((r) =>
          dayjs(r.maintenanceRateApplicableRange[1]).endOf("day").toISOString(),
        ),
      };

      setSubmitting(true);
      const token = window.localStorage.getItem("token");
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/addInvForGeneratingCode`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
          body: JSON.stringify(body),
        },
      );
      const result = await response.json();

      if (result.success) {
        notification.success({
          message: "Success",
          description: result.message || "Code generated successfully.",
        });
        form.resetFields();
        setRows([defaultRow()]);
        setPrefix(undefined);
        setNextSerialNo(null);
      } else {
        notification.error({
          message: "Error",
          description: result.message || "Failed to generate code.",
        });
      }
    } catch (err) {
      if (err?.errorFields) {
        notification.warning({
          message: "Validation",
          description: "Please fill City and Area.",
        });
      } else {
        notification.error({
          message: "Error",
          description: err?.message || "Something went wrong.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const illuminationOptions = [
    { label: "FL", value: "FL" },
    { label: "BL", value: "BL" },
    { label: "NL", value: "NL" },
  ];

  const masterColumns = [
    {
      title: "Sr No.",
      dataIndex: "srNo",
      key: "srNo",
      width: 70,
      fixed: "left",
      align: "center",
    },
    {
      title: "Code No",
      dataIndex: "codeNo",
      key: "codeNo",
      width: 120,
      fixed: "left",
      align: "center",
    },
    { title: "City", dataIndex: "city", key: "city", width: 140, align: "center" },
    { title: "Area", dataIndex: "area", key: "areaZone", width: 160, align: "center" },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 520,
      align: "center",
      onHeaderCell: () => ({
        style: { textAlign: "center" },
      }),
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word", textAlign: "left" }}>
          {text || "-"}
        </div>
      ),
    },
    { title: "W", dataIndex: "w", key: "w", width: 50, align: "center" },
    { title: "H", dataIndex: "h", key: "h", width: 50, align: "center" },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      width: 50,
      align: "center",
    },
    {
      title: "Sq.ft.",
      dataIndex: "sqFt",
      key: "sqFt",
      width: 90,
      align: "center",
    },
    {
      title: "Media",
      dataIndex: "media",
      key: "media",
      width: 120,
      align: "center",
    },
    {
      title: "Illu",
      dataIndex: "illu",
      key: "illu",
      width: 90,
      align: "center",
    },
    {
      title: "Zone",
      dataIndex: "zone",
      key: "zone",
      width: 110,
      align: "center",
    },
    {
      title: "Inv",
      dataIndex: "inv",
      key: "inv",
      width: 100,
      align: "center",
    },
    {
      title: "Purchase P/Month",
      dataIndex: "purchasePerMonth",
      key: "purchasePerMonth",
      width: 140,
      align: "center",
      render: (v) => (v == null ? "-" : Number(v).toLocaleString("en-IN")),
    },
    {
      title: "Rate Per Month",
      dataIndex: "ratePerMonth",
      key: "ratePerMonth",
      width: 120,
      align: "center",
      render: (v) => (v == null ? "-" : Number(v).toLocaleString("en-IN")),
    },
    {
      title: "Site Removed On",
      dataIndex: "siteRemovedOn",
      key: "siteRemovedOn",
      width: 140,
      align: "center",
      render: (v) => v || "-",
    },
  ];
  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
            fontSize: "12px",
            fontWeight: "bold",
            padding: "8px 4px",
          }}
        />
      ),
    },
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = dayjs(dateStr);
    return d.isValid() ? d.format("DD-MM-YYYY") : "-";
  };

  const archivedMasterRowProps = (record) =>
    record?.isArchived === true ? { style: { color: "#ff4d4f" } } : {};

  const archivedRateCardRowProps = (record) =>
    record?.invId?.isArchived === true ? { style: { color: "#ff4d4f" } } : {};

  const rateHistoryTableColumns = (parentRecord) => [
    { title: "Maintenance Rate", dataIndex: "maintenanceRate", key: "maintenanceRate", width: 140, align: "center", render: (v) => (v == null ? "-" : Number(v).toLocaleString("en-IN")) },
    { title: "Start Date", dataIndex: "startDate", key: "startDate", width: 140, align: "center", render: (v) => formatDate(v) },
    { title: "End Date", dataIndex: "endDate", key: "endDate", width: 140, align: "center", render: (v) => formatDate(v) },
    {
      title: "Action",
      key: "action",
      width: 80,
      align: "center",
      render: (_, historyRow) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openRateHistoryEdit(parentRecord, historyRow)}>
          Edit
        </Button>
         <Button 
          type="link" 
          size="small" 
          danger
          icon={<DeleteOutlined />} 
          onClick={() => {
            Modal.confirm({
              title: "Delete Rate History",
              content: "Are you sure you want to delete this rate history entry?",
              okText: "Delete",
              okType: "danger",
              cancelText: "Cancel",
              onOk: () => deleteRateHistory(parentRecord, historyRow._id),
            });
          }}
        >
          Delete
        </Button>
        </div>
      ),
    },
  ];

  const rateCardColumns = [
    { title: "Code No", dataIndex: ["invId", "CodeNo"], key: "codeNo", width: 100, fixed: "left", align: "center" },
    { title: "Area", dataIndex: ["invId", "locations", "Area"], key: "area", width: 120, align: "center" },
    {
      title: "Location",
      key: "locationName",
      width: 280,
      align: "center",
      render: (_, record) => record?.invId?.locations?.name ?? "-",
    },
    { title: "Media", dataIndex: ["invId", "typeOfMedia", "name"], key: "media", width: 100, align: "center" },
    {
      title: "Size",
      key: "size",
      width: 100,
      align: "center",
      render: (_, record) => {
        const w = record?.invId?.width;
        const h = record?.invId?.height;
        if (w == null && h == null) return "-";
        return `${w ?? "-"} x ${h ?? "-"}`;
      },
    },
    {
      title: "Rate/Month",
      dataIndex: ["invId", "ratePerMonth"],
      key: "ratePerMonth",
      width: 120,
      align: "center",
      render: (v) => (v == null ? "-" : Number(v).toLocaleString("en-IN")),
    },
    {
      title: "Current Maintenance",
      dataIndex: "currentMaintenance",
      key: "currentMaintenance",
      width: 140,
      align: "center",
      render: (v) => (v == null ? "-" : Number(v).toLocaleString("en-IN")),
    },
  ];

  // Tab 1 content (your current generate UI, unchanged)
  const renderGenerateTab = () => (
    <Form form={form} layout="vertical">
      <Row gutter={24}>
        <Col span={4}>
          <Form.Item label="Prefix" required>
            <Select
              placeholder="Select Prefix"
              allowClear
              value={prefix}
              onChange={handlePrefixChange}
              loading={loadingSerial}
              style={{ width: "100%" }}
              options={VALID_PREFIXES.map((p) => ({ label: p, value: p }))}
            />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item
            name="city"
            label="City"
            rules={[{ required: true, message: "City is required" }]}
          >
            <Input placeholder="e.g. Mumbai" />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item
            name="area"
            label="Area"
            rules={[{ required: true, message: "Area is required" }]}
          >
            <Input placeholder="e.g. Andheri" />
          </Form.Item>
        </Col>
      </Row>

      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <Button type="dashed" icon={<PlusOutlined />} onClick={addRow}>
            Add row
          </Button>
        </div>

        <Table
          dataSource={rows}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1260 }}
          size="small"
          columns={[
            {
              title: "Location",
              dataIndex: "location",
              width: 300,
              align: "center",
              render: (val, record) => (
                <Input
                  value={val}
                  onChange={(e) =>
                    updateRow(record.id, "location", e.target.value)
                  }
                  placeholder="Enter Location"
                  required
                />
              ),
            },
            {
              title: "Media Type",
              dataIndex: "mediaType",
              width: 100,
              align: "center",
              render: (val, record) => (
                <Select
                  placeholder="Select"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  value={val || undefined}
                  onChange={(v) => updateRow(record.id, "mediaType", v)}
                  options={mediaTypesList.map((m) => ({
                    label: m.name,
                    value: m._id,
                  }))}
                />
              ),
            },
            {
              title: "Illumination",
              dataIndex: "illumination",
              width: 80,
              align: "center",
              render: (val, record) => (
                <Select
                  placeholder="Select"
                  allowClear
                  style={{ width: "100%" }}
                  value={val || undefined}
                  onChange={(v) => updateRow(record.id, "illumination", v)}
                  options={illuminationOptions}
                />
              ),
            },
            {
              title: "Width",
              dataIndex: "width",
              width: 60,
              align: "center",
              render: (val, record) => (
                <InputNumber
                  min={0}
                  controls={false}
                  style={{ width: "100%" }}
                  value={val}
                  onChange={(v) => updateRow(record.id, "width", v)}
                  placeholder="20"
                  required
                />
              ),
            },
            {
              title: "Height",
              dataIndex: "Height",
              width: 60,
              align: "center",
              render: (val, record) => (
                <InputNumber
                  min={0}
                  controls={false}
                  style={{ width: "100%" }}
                  value={val}
                  onChange={(v) => updateRow(record.id, "Height", v)}
                  placeholder="10"
                  required
                />
              ),
            },
            {
              title: (
                <>
                  Suffix
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      fontWeight: "normal",
                    }}
                  >
                    (Optional)
                  </div>
                </>
              ),
              dataIndex: "suffix",
              width: 80,
              align: "center",
              render: (val, record) => (
                <Select
                  placeholder="Suffix"
                  allowClear
                  style={{ width: "100%" }}
                  value={val || undefined}
                  onChange={(v) =>
                    updateRow(record.id, { suffix: v, codeNo: undefined })
                  }
                  options={SUFFIX_OPTIONS}
                />
              ),
            },
            {
              title: "Code No",
              key: "codeNo",
              width: 100,
              align: "center",
              render: (_, record) => {
                const displayValue =
                  record.codeNo !== undefined && record.codeNo !== ""
                    ? record.codeNo
                    : getCodeNoForRow(record);

                return (
                  <Input
                    value={displayValue}
                    onChange={(e) =>
                      updateRow(record.id, "codeNo", e.target.value)
                    }
                    placeholder={
                      prefix && nextSerialNo != null
                        ? `${prefix}-${nextSerialNo}`
                        : "Code No"
                    }
                  />
                );
              },
            },
            {
              title: "Maintenance Rate",
              dataIndex: "maintenanceRate",
              width: 110,
              align: "center",
              render: (val, record) => (
                <InputNumber
                  min={0}
                  controls={false}
                  style={{ width: "100%" }}
                  value={val}
                  onChange={(v) => updateRow(record.id, "maintenanceRate", v)}
                  placeholder="5000"
                  required
                />
              ),
            },
            {
              title: "Maintenance Rate Applicable Duration",
              dataIndex: "maintenanceRateApplicableRange",
              width: 220,
              align: "center",
              render: (val, record) => (
                <DatePicker.RangePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  value={val && val[0] && val[1] ? [dayjs(val[0]), dayjs(val[1])] : null}
                  onChange={(dates) => updateRow(record.id, "maintenanceRateApplicableRange", dates)}
                />
              ),
            },
            {
              title: "",
              key: "action",
              width: 56,
              fixed: "right",
              align: "center",
              render: (_, record) => (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeRow(record.id)}
                  disabled={rows.length <= 1}
                />
              ),
            },
          ]}
          components={components}
        />

        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}
        >
          <Button
            type="primary"
            htmlType="button"
            onClick={handleSubmit}
            loading={submitting}
          >
            Generate Code
          </Button>
        </div>
      </div>
    </Form>
  );

  // Tab 2
  const renderMasterTab = () => (
    <Table
      dataSource={masterData}
      columns={masterColumns}
      loading={masterLoading}
      rowKey={(r) => `${r.codeNo}-${r.srNo}`}
      pagination={false}
      sticky
      scroll={{ x: 2040, y: 500 }}
      // size="small"
      components={components}
      tableLayout="fixed"
      onRow={archivedMasterRowProps}
    />
  );

  // Tab 3 – Rate Card with expandable rate history
  const renderRateCardTab = () => (
    <Table
      dataSource={rateCardData}
      columns={rateCardColumns}
      loading={rateCardLoading}
      rowKey="_id"
      pagination={false}
      scroll={{ x: 1400, y: 500 }}
      components={components}
      tableLayout="fixed"
      onRow={archivedRateCardRowProps}
      expandable={{
        expandedRowRender: (record) => {
          const history = record?.rateHistory;
          const isEmpty = !Array.isArray(history) || history.length === 0;
          return (
            <div style={{ margin: "16px 0 16px 48px" }}>
              <div style={{ marginBottom: 8 }}>
                <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => openRateHistoryAdd(record)}>
                  Add Rate
                </Button>
              </div>
              {isEmpty ? (
                <div style={{ padding: "8px 0", color: "#888" }}>No rate history.</div>
              ) : (
                <Table
                  size="small"
                  dataSource={history}
                  columns={rateHistoryTableColumns(record)}
                  rowKey="_id"
                  pagination={false}
                  style={{ maxWidth: 620 }}
                  components={{
                    header: {
                      cell: (props) => (
                        <th
                          {...props}
                          style={{
                            ...props.style,
                            backgroundColor: "#f4faff",
                            fontSize: "12px",
                            fontWeight: "bold",
                            padding: "8px 4px",
                          }}
                        />
                      ),
                    },
                  }}
                />
              )}
            </div>
          );
        },
        rowExpandable: () => true,
      }}
    />
  );

  const items = [
    { key: "1", label: "Generate Code No" },
    { key: "2", label: "Inventory Master" },
    { key: "3", label: "Rate Card" },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "1":
        return renderGenerateTab();
      case "2":
        return renderMasterTab();
      case "3":
        return renderRateCardTab();
      default:
        return null;
    }
  };

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Tabs
          defaultActiveKey="1"
          activeKey={activeTab}
          onChange={onTabChange}
          items={items}
        />

        {activeTab === "2" && (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Input
              placeholder="Search by Code No / Location"
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              value={masterSearch}
              onChange={(e) => setMasterSearch(e.target.value)}
              style={{ width: 350 }}
            />
            <Button type="primary" icon={<FaDownload />} loading={downloading} onClick={downloadMasterExcel}>
              Download Excel
            </Button>
          </div>
        )}

        {activeTab === "3" && (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Input
              placeholder="Search by Code No / Location"
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              value={rateCardSearch}
              onChange={(e) => setRateCardSearch(e.target.value)}
              style={{ width: 350 }}
            />
            <Button type="primary" icon={<FaDownload />} loading={downloading} onClick={downloadRateCardExcel}>
              Download Excel
            </Button>
          </div>
        )}
      </div>

      {renderTabContent()}

      <Modal
        title={rateHistoryEditItem ? "Edit Maintenance Rate" : "Add Maintenance Rate"}
        open={rateHistoryModalVisible}
        onCancel={closeRateHistoryModal}
        onOk={saveRateHistory}
        confirmLoading={rateCardSaving}
        destroyOnClose
        okText="Save"
      >
        <Form form={rateHistoryForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="maintenanceRate"
            label="Maintenance Rate"
            rules={[{ required: true, message: "Required" }]}
          >
            <InputNumber
              min={0}
              controls={false}
              style={{ width: "100%" }}
              placeholder="e.g. 35000"
              parser={(v) => {
                const digits = String(v ?? "").replace(/\D/g, "");
                return digits === "" ? undefined : Number(digits);
              }}
              onKeyDown={(e) => {
                const allowed = ["Backspace", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Delete"];
                if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
                if (!/^\d$/.test(e.key)) e.preventDefault();
              }}
            />
          </Form.Item>
          <Form.Item
            name="startDate"
            label="Start Date"
            rules={[{ required: true, message: "Required" }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
          </Form.Item>
          <Form.Item
            name="endDate"
            label="End Date"
            rules={[{ required: true, message: "Required" }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InventoryMasterContainer;
